(() => {
  const runtime = window.AuremeStaffstartDefaultOverrides?.runtime;
  if (!runtime) return;

  const {
    createEmptyMarkedElements,
    findStaffProfileSummaryRow,
    getStaffDetailId,
    isStaffDetailPath,
    patchOrder,
    registerPatch,
  } = runtime;

  const STAFFSTART_API_ORIGIN = 'https://api.staff-start.com';
  const STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE = 'data-aureme-staffstart-tiktok-link';
  const STAFF_SOCIAL_TIKTOK_REQUEST_KEY = 'auremeStaffstartTikTokRequestKey';
  const STAFF_SOCIAL_TIKTOK_RETRY_AFTER_KEY = 'auremeStaffstartTikTokRetryAfter';
  const STAFF_SOCIAL_TIKTOK_RETRY_COOLDOWN_MS = 30 * 1000;
  const STAFF_SOCIAL_TIKTOK_FETCH_TIMEOUT_MS = 8 * 1000;
  const STAFF_SOCIAL_YOUTUBE_HANDLE_PATH_PATTERN = /^\/user\/(@[^/]+)\/?$/;
  const STAFF_SOCIAL_YOUTUBE_LINK_SELECTOR = [
    'a[href^="https://www.youtube.com/"]',
    'a[href^="https://youtube.com/"]',
  ].join(',');
  const STAFF_SOCIAL_LINK_SELECTOR = [
    'a[href^="https://instagram.com/"]',
    'a[href^="https://www.instagram.com/"]',
    'a[href^="https://twitter.com/"]',
    'a[href^="https://www.twitter.com/"]',
    'a[href^="https://facebook.com/"]',
    'a[href^="https://www.facebook.com/"]',
    STAFF_SOCIAL_YOUTUBE_LINK_SELECTOR,
  ].join(',');
  const staffTikTokProfileCache = new Map();
  const staffTikTokProfileRetryAfter = new Map();

  /**
   * @typedef {object} StaffTikTokProfile
   * @property {string} href - TikTok アカウント URL
   * @property {string} label - 表示ラベル
   */

  /**
   * STAFF START 設定から merchant ID を返す。
   * @returns {string}
   */
  function getStaffstartMerchantId() {
    const merchantId = window.StaffStart?.merchantId;

    return typeof merchantId === 'string' || typeof merchantId === 'number'
      ? String(merchantId).trim()
      : '';
  }

  /**
   * TikTok の href かどうかを返す。
   * @param {string} href - 判定対象 URL
   * @returns {boolean}
   */
  function isTikTokHref(href) {
    try {
      const url = new URL(href);
      const hostname = url.hostname.toLowerCase();

      return hostname === 'tiktok.com' || hostname.endsWith('.tiktok.com');
    } catch {
      return false;
    }
  }

  /**
   * YouTube の URL かどうかを返す。
   * @param {URL} url - 判定対象 URL
   * @returns {boolean}
   */
  function isYouTubeUrl(url) {
    const hostname = url.hostname.toLowerCase();

    return hostname === 'youtube.com' || hostname.endsWith('.youtube.com');
  }

  /**
   * StaffStart が user path で出力した YouTube handle URL を公式 handle URL に正規化する。
   * @param {string} href - app 標準の YouTube href
   * @returns {string}
   */
  function createCanonicalYouTubeHandleHref(href) {
    try {
      const url = new URL(href);
      if (url.protocol !== 'https:' || !isYouTubeUrl(url)) return '';

      const handleMatch = url.pathname.match(STAFF_SOCIAL_YOUTUBE_HANDLE_PATH_PATTERN);
      if (!handleMatch) return '';

      url.hostname = 'www.youtube.com';
      url.pathname = `/${handleMatch[1]}`;

      return url.href;
    } catch {
      return '';
    }
  }

  /**
   * スタッフ詳細の YouTube handle リンクを、遷移可能な公式 URL に補正する。
   * @param {HTMLElement} container - STAFF START SNS リンクの親ブロック
   */
  function normalizeStaffYouTubeLinks(container) {
    for (const link of container.querySelectorAll(STAFF_SOCIAL_YOUTUBE_LINK_SELECTOR)) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const canonicalHref = createCanonicalYouTubeHandleHref(link.href);
      if (canonicalHref) {
        link.href = canonicalHref;
      }
    }
  }

  /**
   * TikTok の handle として扱える値を返す。
   * @param {string} value - handle 候補
   * @returns {string}
   */
  function normalizeTikTokHandle(value) {
    const handle = value.replace(/^\/+/, '').replace(/^@/, '').replace(/\/+$/, '').trim();

    return /^[A-Za-z0-9._]+$/.test(handle) ? handle : '';
  }

  /**
   * StaffStart の TikTok link_value を外部リンク URL に正規化する。
   * @param {unknown} linkValue - StaffStart API の sns.tiktok.link_value
   * @returns {string}
   */
  function createTikTokHref(linkValue) {
    if (typeof linkValue !== 'string') return '';

    const value = linkValue.trim();
    if (!value) return '';

    try {
      const url = new URL(value);
      if (url.protocol !== 'https:' || !isTikTokHref(url.href)) return '';

      const handle = normalizeTikTokHandle(url.pathname);
      return handle ? `https://www.tiktok.com/@${encodeURIComponent(handle)}` : '';
    } catch {
      const handle = normalizeTikTokHandle(value);
      if (!handle) return '';

      return `https://www.tiktok.com/@${encodeURIComponent(handle)}`;
    }
  }

  /**
   * StaffStart API レスポンスから TikTok 表示情報を取り出す。
   * @param {unknown} data - StaffStart API レスポンス
   * @returns {StaffTikTokProfile | null}
   */
  function extractStaffTikTokProfile(data) {
    const staff = Array.isArray(data?.item) ? data.item[0] : null;
    const tiktok = staff?.sns?.tiktok;
    if (!tiktok) return null;

    const href = createTikTokHref(tiktok.link_value);
    if (!href) return null;

    const label =
      typeof tiktok.display_value === 'string' && tiktok.display_value.trim()
        ? tiktok.display_value.trim()
        : 'TikTok';

    return { href, label };
  }

  /**
   * StaffStart API からスタッフの TikTok 表示情報を取得する。
   * @param {string} merchantId - StaffStart merchant ID
   * @param {string} staffId - StaffStart staff ID
   * @returns {Promise<StaffTikTokProfile | null>}
   */
  async function fetchStaffTikTokProfile(merchantId, staffId) {
    const params = new URLSearchParams({
      merchant_id: merchantId,
      user_id: staffId,
    });
    const requestUrl = `${STAFFSTART_API_ORIGIN}/v1/staff/detail?${params.toString()}`;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeoutId = controller
      ? window.setTimeout(() => controller.abort(), STAFF_SOCIAL_TIKTOK_FETCH_TIMEOUT_MS)
      : 0;
    let response;

    try {
      response = await fetch(requestUrl, controller ? { signal: controller.signal } : undefined);
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw new Error(`StaffStart staff detail API failed: ${response.status}`);
    }

    return extractStaffTikTokProfile(await response.json());
  }

  /**
   * staff 単位の TikTok 情報キャッシュキーを返す。
   * @param {string} merchantId - StaffStart merchant ID
   * @param {string} staffId - StaffStart staff ID
   * @returns {string}
   */
  function createStaffTikTokProfileCacheKey(merchantId, staffId) {
    return `${merchantId}:${staffId}`;
  }

  /**
   * 同一 staff の TikTok 情報を二重取得しないようキャッシュして返す。
   * @param {string} merchantId - StaffStart merchant ID
   * @param {string} staffId - StaffStart staff ID
   * @returns {Promise<StaffTikTokProfile | null>}
   */
  function getStaffTikTokProfile(merchantId, staffId) {
    const cacheKey = createStaffTikTokProfileCacheKey(merchantId, staffId);
    if (Date.now() < (staffTikTokProfileRetryAfter.get(cacheKey) || 0)) {
      return Promise.reject(new Error('StaffStart TikTok profile retry cooldown'));
    }

    if (!staffTikTokProfileCache.has(cacheKey)) {
      staffTikTokProfileCache.set(
        cacheKey,
        fetchStaffTikTokProfile(merchantId, staffId)
          .then((profile) => {
            staffTikTokProfileRetryAfter.delete(cacheKey);
            return profile;
          })
          .catch((error) => {
            staffTikTokProfileCache.delete(cacheKey);
            staffTikTokProfileRetryAfter.set(
              cacheKey,
              Date.now() + STAFF_SOCIAL_TIKTOK_RETRY_COOLDOWN_MS,
            );
            throw error;
          }),
      );
    }

    return staffTikTokProfileCache.get(cacheKey);
  }

  /**
   * app 標準の SNS リンクが空でも残る、スタッフ詳細プロフィール右側のブロックを返す。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {HTMLElement | null}
   */
  function findStaffProfileSocialContainer(root) {
    const summaryRow = findStaffProfileSummaryRow(root);
    if (!summaryRow) return null;

    const socialContainer = summaryRow.children[1];
    return socialContainer instanceof HTMLElement ? socialContainer : null;
  }

  /**
   * app 標準のスタッフ SNS リンクを含む親ブロックを一覧する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @returns {Set<HTMLElement>}
   */
  function collectStaffSocialContainers(root) {
    const containers = new Set();
    const profileSocialContainer = findStaffProfileSocialContainer(root);
    if (profileSocialContainer) {
      containers.add(profileSocialContainer);
      return containers;
    }

    for (const link of root.querySelectorAll(STAFF_SOCIAL_LINK_SELECTOR)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (!(link.parentElement instanceof HTMLElement)) continue;

      containers.add(link.parentElement);
    }

    return containers;
  }

  /**
   * app 標準の TikTok リンクがあるかどうかを返す。
   * @param {HTMLElement} container - STAFF START SNS リンクの親ブロック
   * @returns {boolean}
   */
  function hasNativeTikTokLink(container) {
    return Array.from(container.querySelectorAll('a[href]')).some((link) => {
      return (
        link instanceof HTMLAnchorElement &&
        !link.hasAttribute(STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE) &&
        isTikTokHref(link.href)
      );
    });
  }

  /**
   * 補正で追加した TikTok リンクを外す。
   * @param {HTMLElement} container - STAFF START SNS リンクの親ブロック
   */
  function removeStaffTikTokLink(container) {
    const link = container.querySelector(`[${STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE}]`);
    if (link instanceof HTMLAnchorElement) {
      link.remove();
    }
  }

  /**
   * 補正で追加した TikTok リンクが残っているかどうかを返す。
   * @param {HTMLElement} container - STAFF START SNS リンクの親ブロック
   * @returns {boolean}
   */
  function hasStaffTikTokLink(container) {
    return Boolean(container.querySelector(`[${STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE}]`));
  }

  /**
   * 補正で追加した TikTok リンクを作る。
   * @param {StaffTikTokProfile} profile - TikTok 表示情報
   * @returns {HTMLAnchorElement}
   */
  function createStaffTikTokLink(profile) {
    const link = document.createElement('a');
    link.href = profile.href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute(STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE, '');
    link.setAttribute('aria-label', `TikTok: ${profile.label}`);

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 20 20');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute(
      'd',
      'M10.511 1.705h2.74s-.157 3.51 3.795 3.768v2.711s-2.114.129-3.796-1.158l.028 5.606A5.073 5.073 0 1 1 8.213 7.56h.708v2.785a2.298 2.298 0 1 0 1.618 2.205z',
    );

    icon.appendChild(path);
    link.appendChild(icon);
    link.appendChild(document.createTextNode(profile.label));

    return link;
  }

  /**
   * StaffStart の非同期描画後に、スタッフ詳細の TikTok リンクを補完する。
   * @param {HTMLElement} container - STAFF START SNS リンクの親ブロック
   * @param {string} merchantId - StaffStart merchant ID
   * @param {string} staffId - StaffStart staff ID
   */
  function ensureStaffTikTokLink(container, merchantId, staffId) {
    /*
     * soft navigation 中に前 staff の取得結果が戻るため、staff 単位の requestKey を DOM に残す。
     * then/catch 側でも現在 route と照合し、古い補完リンクや retry cooldown を反映しない。
     */
    const requestKey = `${merchantId}:${staffId}`;
    const cacheKey = createStaffTikTokProfileCacheKey(merchantId, staffId);
    const retryAfter = Number(container.dataset[STAFF_SOCIAL_TIKTOK_RETRY_AFTER_KEY] || 0);
    const hasInjectedLink = hasStaffTikTokLink(container);
    if (container.dataset[STAFF_SOCIAL_TIKTOK_REQUEST_KEY] !== requestKey) {
      removeStaffTikTokLink(container);
    }
    if (
      container.dataset[STAFF_SOCIAL_TIKTOK_REQUEST_KEY] === requestKey &&
      (hasInjectedLink || Date.now() < retryAfter)
    ) {
      return;
    }

    container.dataset[STAFF_SOCIAL_TIKTOK_REQUEST_KEY] = requestKey;
    delete container.dataset[STAFF_SOCIAL_TIKTOK_RETRY_AFTER_KEY];
    getStaffTikTokProfile(merchantId, staffId)
      .then((profile) => {
        if (!container.isConnected) return;
        if (getStaffDetailId() !== staffId) return;
        if (!profile || hasNativeTikTokLink(container)) {
          removeStaffTikTokLink(container);
          return;
        }
        if (hasStaffTikTokLink(container)) return;

        container.appendChild(createStaffTikTokLink(profile));
      })
      .catch(() => {
        if (!container.isConnected) return;
        if (getStaffDetailId() !== staffId) return;
        if (container.dataset[STAFF_SOCIAL_TIKTOK_REQUEST_KEY] === requestKey) {
          container.dataset[STAFF_SOCIAL_TIKTOK_RETRY_AFTER_KEY] = String(
            staffTikTokProfileRetryAfter.get(cacheKey) ||
              Date.now() + STAFF_SOCIAL_TIKTOK_RETRY_COOLDOWN_MS,
          );
        }
      });
  }

  /**
   * スタッフ詳細の SNS リンク補正状態を同期する。
   * @param {HTMLElement} root - STAFF START アプリの描画ルート
   * @param {Set<HTMLElement>} elements - SNS リンクの親ブロック
   */
  function syncStaffSocialLinks(root, elements) {
    for (const link of root.querySelectorAll(`[${STAFF_SOCIAL_TIKTOK_LINK_ATTRIBUTE}]`)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (link.parentElement instanceof HTMLElement && elements.has(link.parentElement)) continue;

      link.remove();
    }

    for (const element of elements) {
      normalizeStaffYouTubeLinks(element);
    }

    const staffId = getStaffDetailId();
    const merchantId = getStaffstartMerchantId();
    if (!staffId || !merchantId) return;

    for (const element of elements) {
      if (hasNativeTikTokLink(element)) {
        removeStaffTikTokLink(element);
        delete element.dataset[STAFF_SOCIAL_TIKTOK_REQUEST_KEY];
        delete element.dataset[STAFF_SOCIAL_TIKTOK_RETRY_AFTER_KEY];
        continue;
      }

      ensureStaffTikTokLink(element, merchantId, staffId);
    }
  }

  registerPatch({
    order: patchOrder.staffDetail.socialLinks,
    isTargetRoute: isStaffDetailPath,
    collect: collectStaffSocialContainers,
    createEmptyTargets: createEmptyMarkedElements,
    apply: syncStaffSocialLinks,
  });
})();
